import React, { useState, useMemo, type ReactNode } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import useCan from '@shared/permissions/useCan'
import type { Permission } from '@/permissions_manifest.generated'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { downloadImportTemplate } from '@utils/downloadTemplate'

interface ContactAddressService extends TableService {
  import: (formData: FormData) => Promise<unknown>;
  export: (ids: string[], language: string) => Promise<unknown>;
}

interface SalesChannelService {
  list: (params: Record<string, unknown>) => Promise<{ data: Array<{ id: string; name: string }> }>;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ContactAddressRow {
  id: string;
  code?: string;
  merchant_name?: string;
  receiver_name?: string;
  receiver_phone?: string;
  state?: string;
  city?: string;
  district?: string;
  postal_code?: string;
  country_code?: string;
  address?: string;
  default_sales_channel?: { name?: string } | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface ContactAddressListProps {
  role?: 'admin' | 'operator';
  service: ContactAddressService; // New prop
  salesChannelService: SalesChannelService; // New prop
  addJob?: (jobId: string, jobName: string, ...args: any[]) => void;
  merchants?: MerchantOption[];
  importModalVisible?: boolean;
  setImportModalVisible?: (visible: boolean) => void;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
  // admin caller 仍會傳 showMerchantColumn={false}，雖然此元件內部不讀；保留 prop 以避免 TS 報錯
  showMerchantColumn?: boolean;
}

interface BatchAction {
  name: string;
  className?: string;
  requiredPermission?: Permission;
  do: (selected: Iterable<string>) => void;
}

const ContactAddressList: React.FC<ContactAddressListProps> = ({
  role = 'admin',
  service, // New prop
  salesChannelService, // New prop
  addJob,
  merchants = [],
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t, i18n } = useTranslation()
  const can = useCan()
  const [lastModified, setLastModified] = useState(0)
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // Support external or internal import modal control
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  // 匯入功能（使用 Context 共享背景任務管理器）
  // useImport 來自 .js，TS 從 JSDoc 推出的 signature 不對；用 cast 回到實際 runtime shape
  const useImportTyped = useImport as unknown as (cfg: Record<string, unknown>) => (file: File, merchantId: string, overrideExisting: boolean) => Promise<void>
  const handleImport = useImportTyped({
    action: (formData: FormData) => service.import(formData),
    addJob,
    jobName: 'shared.contact_addresses.list.import.job_name',
    i18nKeys: {
      noFile: 'shared.contact_addresses.list.import.no_file',
      invalidFile: 'shared.contact_addresses.list.import.invalid_file',
      success: 'shared.contact_addresses.list.import.success',
      error: 'shared.contact_addresses.list.import.error',
    }
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: File, merchantId: string, overrideExisting: boolean) => {
    await handleImport(file, merchantId, overrideExisting)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId?: string) => {
    await downloadImportTemplate({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.contact_addresses.list.template.success',
      errorKey: 'shared.contact_addresses.list.template.error'
    })
  }

  // 表格欄位定義
  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: `models.contact_address.code`,
        persist: true,
        sortable: true,
        sortFieldName: 'contact_addresses.code',
        width: '120px',
        cell: ({ id, code }: ContactAddressRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/contact_addresses/${id}`}>
              {code}
            </Link>
            <a
              href={`${urlPrefix}/contact_addresses/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `models.contact_address.merchant`,
        width: '150px',
        cell: ({ merchant_name }: ContactAddressRow) => merchant_name || '-',
      });
    }

    cols.push(
      {
        nameKey: `models.contact_address.receiver_name`,
        sortable: true,
        sortFieldName: 'contact_addresses.receiver_name',
        width: '150px',
        selector: ({ receiver_name }: ContactAddressRow) => receiver_name,
      },
      {
        nameKey: `models.contact_address.receiver_phone`,
        width: '130px',
        selector: ({ receiver_phone }: ContactAddressRow) => receiver_phone,
      },
      {
        nameKey: `models.contact_address.city`,
        width: '100px',
        selector: ({ city }: ContactAddressRow) => city,
      },
      {
        nameKey: `models.contact_address.district`,
        width: '100px',
        selector: ({ district }: ContactAddressRow) => district,
      },
      {
        nameKey: `models.contact_address.postal_code`,
        width: '80px',
        selector: ({ postal_code }: ContactAddressRow) => postal_code,
      },
      {
        nameKey: `models.contact_address.country_code`,
        width: '100px',
        selector: ({ country_code }: ContactAddressRow) => country_code,
      },
      {
        nameKey: `models.contact_address.address`,
        sortable: false,
        wrap: true,
        selector: ({ address }: ContactAddressRow) => address,
      },
      {
        nameKey: `models.contact_address.default_channel`,
        sortable: false,
        width: '150px',
        cell: ({ default_sales_channel }: ContactAddressRow) => {
          if (!default_sales_channel?.name) return '-'
          return (
            <Tag color="blue">
              {default_sales_channel.name}
            </Tag>
          )
        },
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'contact_addresses.created_at',
        width: '150px',
        selector: ({ created_at }: ContactAddressRow) => created_at,
      }
    );

    // Add updated_at column only for admin
    if (role === 'admin') {
      cols.push({
        nameKey: `common.updated_at`,
        sortable: true,
        sortFieldName: 'contact_addresses.updated_at',
        width: '150px',
        selector: ({ updated_at }: ContactAddressRow) => updated_at,
      });
    }

    return cols;
  }, [role, urlPrefix, hideMerchantUI])

  // 篩選條件定義
  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t(`models.contact_address.code`),
        component: MultipleValue,
        inputName: 'contact_addresses.code',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.contact_addresses.list.filter_tooltips.code.title'),
          content: t('shared.contact_addresses.list.filter_tooltips.code.content'),
        },
      },
      {
        label: t(`models.contact_address.receiver_name`),
        component: MultipleValue,
        inputName: 'contact_addresses.receiver_name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.contact_addresses.list.filter_tooltips.receiver_name.title'),
          content: t('shared.contact_addresses.list.filter_tooltips.receiver_name.content'),
        },
      },
      {
        label: t(`models.contact_address.receiver_phone`),
        component: MultipleValue,
        inputName: 'contact_addresses.receiver_phone',
        createable: true,
        tooltip: {
          title: t('shared.contact_addresses.list.filter_tooltips.receiver_phone.title'),
          content: t('shared.contact_addresses.list.filter_tooltips.receiver_phone.content'),
        },
      },
      {
        label: t(`models.contact_address.city`),
        component: MultipleValue,
        inputName: 'contact_addresses.city',
        createable: true,
        tooltip: {
          title: t('shared.contact_addresses.list.filter_tooltips.city.title'),
          content: t('shared.contact_addresses.list.filter_tooltips.city.content'),
        },
      },
      {
        label: t(`models.contact_address.country_code`),
        component: MultipleValue,
        inputName: 'contact_addresses.country_code',
        createable: true,
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`models.contact_address.merchant`),
        component: MultipleValue,
        inputName: 'contact_addresses.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.contact_addresses.list.filter_tooltips.merchant.title'),
          content: t('shared.contact_addresses.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push({
      label: t(`models.contact_address.default_sales_channel`),
      component: MultipleValue,
      inputName: 'contact_addresses.default_sales_channel_id',
      createable: false,
      lazyLoad: {
        action: (params: Record<string, unknown>) => salesChannelService.list(params),
        transform: (data: { data: Array<{ id: string; name: string }> }) => data.data.map((channel) => ({
          label: channel.name,
          value: channel.id,
        })),
      },
      tooltip: {
        title: t('shared.contact_addresses.list.filter_tooltips.default_sales_channel.title'),
        content: t('shared.contact_addresses.list.filter_tooltips.default_sales_channel.content'),
      },
    });

    return rules;
  }, [role, t, merchants, salesChannelService, hideMerchantUI])

  // 操作按鈕定義
  const actions = useMemo(() => ([
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      requiredPermission: 'contact_addresses:export',
      do: (selected: Iterable<string>) => executeBatchOperation({
        task: () => service.export(Array.from(selected), i18n.language),
        jobName: 'common.button.export'
      })
    }
  ] satisfies BatchAction[]).filter(action => !action.requiredPermission || can(action.requiredPermission)),
  [executeBatchOperation, service, i18n, can])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={true}
        columnsEditable={true}
        tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ContactAddressesTable`}
        defaultQuery={{
          sorting_field: 'contact_addresses.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.contact_addresses.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/contact_addresses/list#features`}
      />

      {/* 匯入 Modal */}
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={merchants}
        title={t('shared.contact_addresses.list.import.modal_title') as string}
      />
    </>
  )
}

export default ContactAddressList
